import {
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckOverflow from "../../utils/CheckOverflow";
import TaskSheetReportAndResolve from "../TaskSheetReportAndResolve";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import { handleToastMsg } from "../../components/Toast";
import { acquireSocket, releaseSocket } from "../../utils/socket";
import { getReportIssueData } from "../TaskSheet/mutation";
import Loader from "../../components/Loader";
import { defaultSeconds } from "../../config/masterdata";
import {
  STATUS_COLORS,
  isOpenIssue,
  statusStyle,
} from "../../utils/issueStatus";
import {
  formatReportedDate,
  formatReportedTime,
} from "../../utils/taskSheetDate";

// Sent by the server whenever a task sheet issue is created or saved
const ISSUE_CHANGED_EVENT = "reportIssue:changed";

// Whether the manager can resolve with their solution entry: no solution yet,
// or their solution was rejected (submitted and awaiting approval: no)
const canResolve = (response) =>
  Boolean(response) &&
  ((response.accepted === null && response.solution === null) ||
    (response.accepted === false && response.solution !== null));

function TaskSheetManager({
  setSecondsForFirstInterval,
  isTelevision,
  setPauseInterval,
  pauseInterval,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  // ?slNo= opens the list on one ticket; with action=resolve (Resolve on the
  // alert popups) its resolve form opens straight away
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedSlNo = isTelevision ? "" : searchParams.get("slNo") || "";
  const linkedAction = isTelevision ? "" : searchParams.get("action") || "";
  const [filterSlNo, setFilterSlNo] = useState(linkedSlNo);
  const [statusFilter, setStatusFilter] = useState("All");

  // Popup link followed while this page is already open
  useEffect(() => {
    if (linkedSlNo) {
      setFilterSlNo(linkedSlNo);
      setStatusFilter("All");
      setPage(0);
    }
  }, [linkedSlNo]);

  const managerId = parseInt(localStorage.getItem("id"), 10);

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const [isForm, setIsform] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [isSeek, setIsSeek] = useState(false);
  const [isResolve, setIsResolve] = useState(false);

  const [showIssueData, setShowIssueData] = useState({});

  const isMD = localStorage.getItem("roles") === "MANAGING_DIRECTOR";

  const {
    data: reportIssueData,
    isLoading: isReportDataLoading,
    isFetching: isReportDataFetching,
    dataUpdatedAt,
    errorUpdatedAt,
    refetch,
  } = useQuery(
    "task-sheets-report-issue",
    async () => {
      if (!isTelevision && !isManager) {
        setIsLoadingData(true);
      }
      try {
        const response = await getReportIssueData();
        return response;
      } catch (error) {
        throw error;
      } finally {
        if (!isTelevision && !isManager) {
          setIsLoadingData(false);
        }
      }
    },
    {
      refetchOnMount: true,
      // refetchInterval: 5000, // Poll every 5 seconds (5000ms)
    }
  );

  //this is for make fresh api call when component render and it will avoid the cache
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Live updates without a reload: refresh when a ticket in this list is
  // saved (accepted, declined, resolved by another manager...). New tickets
  // assigned here refresh through their alert popup.
  const listedIdsRef = useRef(new Set());
  useEffect(() => {
    listedIdsRef.current = new Set(
      (reportIssueData?.data || []).map((item) => item?.id)
    );
  }, [reportIssueData]);

  useEffect(() => {
    let timer = null;
    const onIssueChanged = ({ id } = {}) => {
      if (!listedIdsRef.current.has(id)) return;
      // Several saves in a row: one refresh
      clearTimeout(timer);
      timer = setTimeout(() => refetch(), 1000);
    };

    const socket = acquireSocket();
    socket.on(ISSUE_CHANGED_EVENT, onIssueChanged);
    return () => {
      clearTimeout(timer);
      socket.off(ISSUE_CHANGED_EVENT, onIssueChanged);
      releaseSocket();
    };
  }, [refetch]);

  //this is for pause the setTimeout when filter the data
  useEffect(() => {
    if (isTelevision) {
      if (filterSlNo) {
        setPauseInterval(true);
      } else if (!filterSlNo) {
        setPauseInterval(false);
      }
    }
  }, [filterSlNo, isTelevision, setPauseInterval]);

  // Set the interval based on the length of the data for television ui change
  useEffect(() => {
    if (filteredData && isTelevision) {
      setSecondsForFirstInterval(() => {
        //this is for to get the average no of data that we show in the ui
        const dataLength = Math.ceil(filteredData?.length / rowsPerPage);

        return dataLength * defaultSeconds;
      });
    }
  }, [filteredData, isTelevision, rowsPerPage, setSecondsForFirstInterval]);

  // Set the interval based on the length of the data in this ui change
  useEffect(() => {
    if (isTelevision) {
      const totalPages = Math.ceil(filteredData?.length / rowsPerPage);

      const intervalId = setInterval(() => {
        if (!pauseInterval) {
          setPage((prevPage) => (prevPage + 1) % totalPages);
        }
      }, defaultSeconds); // Switch data every 20 seconds

      return () => clearInterval(intervalId);
    }
  }, [filteredData, isTelevision, pauseInterval, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    if (isNaN(event.target.value)) {
      alert("Please enter a number only");
      return;
    }

    setFilterSlNo(event.target.value);
    setPage(0);
  };

  const handleResetFilter = () => {
    setFilterSlNo("");
    setPage(0);
  };

  const handleStatusFilterChange = (event, newStatus) => {
    if (newStatus !== null) {
      setStatusFilter(newStatus);
      setPage(0);
    }
  };

  //this is for pretend the rerender issue when we give checkoverflow params

  useEffect(() => {
    let newFilteredData = [];

    if (isTelevision || isMD) {
      //for television we should show the open (PENDING / REJECTED) Data only
      newFilteredData =
        reportIssueData?.data?.filter((row) => {
          const matchesFilterTerm =
            !filterSlNo || row?.slNo?.toString() === filterSlNo;

          const matchesPendingOnly = isOpenIssue(row.status);

          return matchesFilterTerm && matchesPendingOnly;
        }) || [];
    } else {
      //for manager we can show all the data
      newFilteredData =
        reportIssueData?.data?.filter((row) => {
          const matchesFilterTerm =
            !filterSlNo || row.slNo.toString() === filterSlNo;

          const matchesStatusFilter =
            statusFilter === "All" || row.status === statusFilter;

          return matchesFilterTerm && matchesStatusFilter;
        }) || [];
    }

    setFilteredData(newFilteredData);
  }, [filterSlNo, isMD, isTelevision, reportIssueData?.data, statusFilter]);

  //handle overflow text
  const [isOverflowDraft, addDraftRef] = CheckOverflow(filteredData);

  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverContent, setPopoverContent] = useState("");

  const handlePopoverOpen = (event, id, content) => {
    if (isOverflowDraft[id]) {
      setAnchorEl(event.currentTarget);
      setPopoverContent(content);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverContent("");
  };

  const open = Boolean(anchorEl);

  const handleResolve = (row) => {
    setShowIssueData(row);
    setIsform(true);
    setIsManager(true);
    setIsResolve(true);
    setIsSeek(false);
    if (isTelevision) {
      setPauseInterval(true);
    }
  };

  const handleSeek = (row) => {
    setShowIssueData(row);
    setIsform(true);
    setIsManager(true);
    setIsSeek(true);
    setIsResolve(false);
    if (isTelevision) {
      setPauseInterval(true);
    }
  };

  // Resolve link from a popup: re-fetch and open the form only from issues
  // loaded after the link was followed. The page may already be open with a
  // list from before the rejection, which would still show the solution as
  // awaiting approval. Then drop action from the URL so Back / refresh show
  // the list instead.
  const linkFetchRef = useRef(null); // dataUpdatedAt when the link was followed
  useEffect(() => {
    if (linkedAction !== "resolve" || !linkedSlNo) {
      linkFetchRef.current = null;
      return;
    }
    if (linkFetchRef.current === null) {
      linkFetchRef.current = dataUpdatedAt;
      refetch();
      return;
    }
    if (isReportDataFetching) return;

    const loadFailed = errorUpdatedAt > linkFetchRef.current;
    if (dataUpdatedAt <= linkFetchRef.current && !loadFailed) return;
    linkFetchRef.current = null;
    setSearchParams({ slNo: linkedSlNo }, { replace: true });

    if (loadFailed) {
      handleToastMsg("Unable to load the ticket. Please try again.", "error");
      return;
    }

    const row = reportIssueData?.data?.find(
      (item) => String(item?.slNo) === linkedSlNo
    );
    const response = row?.reportingManagerSolution?.find(
      (item) => parseInt(item?.id, 10) === managerId
    );
    if (row && canResolve(response)) {
      handleResolve(row);
    } else {
      handleToastMsg(
        row
          ? `Ticket ${linkedSlNo} has nothing for you to resolve right now`
          : `Ticket ${linkedSlNo} is not assigned to you`,
        "info"
      );
    }
    // handleResolve only sets state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    linkedAction,
    linkedSlNo,
    reportIssueData,
    dataUpdatedAt,
    errorUpdatedAt,
    isReportDataFetching,
    managerId,
    refetch,
    setSearchParams,
  ]);

  const goBack = () => {
    setIsform(false);
    setIsManager(false);
    setIsResolve(false);
    setIsSeek(false);
    setShowIssueData({});
    if (isTelevision) {
      setPauseInterval(false);
    }
  };

  //handle show image in television only
  const [openNew, setOpenNew] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const handleClickOpen = (image) => {
    setSelectedImage(image);
    setOpenNew(true);
    if (isTelevision) {
      setPauseInterval(true);
    }
  };

  const handleClose = () => {
    setOpenNew(false);
    if (isTelevision) {
      setPauseInterval(false);
    }
  };

  // Full-page loader only until there is a list; later refreshes (live
  // updates, popup links) keep it on screen
  if (isReportDataLoading || (isLoadingData && !reportIssueData)) {
    return <Loader />;
  }

  return (
    <Grid
      container
      item
      xs={12}
      alignContent={"flex-start"}
      rowGap={4}
      sx={{
        padding: "36px",
        "@media (max-width: 600px)": {
          padding: "0px 8px 0px 15px",
        },
      }}
    >
      {isForm ? (
        <TaskSheetReportAndResolve
          // A fresh form per ticket (a popup link can switch tickets)
          key={showIssueData?.id}
          isManager={isManager}
          isSeek={isSeek}
          isResolve={isResolve}
          goBack={goBack}
          showIssueData={showIssueData}
          isTelevision={isTelevision}
        />
      ) : (
        <Grid container item xs={12} rowGap={4}>
          {!isTelevision && !isMD && (
            <Grid
              container
              item
              xs={12}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid container item md={6} sm={6} xs={8}>
                <TextField
                  fullWidth
                  placeholder="Enter Sl No to filter"
                  value={filterSlNo}
                  onChange={handleFilterChange}
                  InputProps={{
                    endAdornment: filterSlNo && (
                      <InputAdornment position="end">
                        <IconButton onClick={handleResetFilter}>
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}

          {!isTelevision && !isMD && (
            <Grid container item md={6} sm={6} xs={9}>
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={handleStatusFilterChange}
                aria-label="status filter"
                fullWidth
                style={{ overflowX: "auto" }}
              >
                <ToggleButton value="All" aria-label="all">
                  All
                </ToggleButton>
                <ToggleButton value="PENDING" aria-label="pending">
                  Pending
                </ToggleButton>
                <ToggleButton value="REJECTED" aria-label="rejected">
                  Rejected
                </ToggleButton>
                <ToggleButton value="RESOLVED" aria-label="resolved">
                  Resolved
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}

          <Grid container item md={12} sm={11} xs={9}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell style={{ maxWidth: "70px", fontWeight: "600" }}>
                      SL NO
                    </TableCell>
                    <TableCell style={{ maxWidth: "70px", fontWeight: "600" }}>
                      DATE
                    </TableCell>
                    <TableCell style={{ maxWidth: "100px", fontWeight: "600" }}>
                      DESCRIPTION
                    </TableCell>
                    <TableCell style={{ maxWidth: "100px", fontWeight: "600" }}>
                      DEPARTMENT
                    </TableCell>
                    <TableCell style={{ maxWidth: "100px", fontWeight: "600" }}>
                      REPORTED BY
                    </TableCell>
                    {(isTelevision || isMD) && (
                      <TableCell
                        style={{ maxWidth: "100px", fontWeight: "600" }}
                      >
                        IMAGE
                      </TableCell>
                    )}
                    <TableCell style={{ maxWidth: "220px", fontWeight: "600" }}>
                      ACTION / STATUS
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData?.length > 0 ? (
                    filteredData
                      ?.sort((a, b) => new Date(b.date) - new Date(a.date))
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((row) => {
                        const isManagerResponse =
                          row?.reportingManagerSolution?.filter(
                            (item) => parseInt(item.id, 10) === managerId
                          );

                        const showSeekDateSelect =
                          isManagerResponse?.length > 0
                            ? (isManagerResponse[0]?.seekedDate === null &&
                                isManagerResponse[0]?.accepted === null &&
                                isManagerResponse[0]?.solution === null) ||
                              (isManagerResponse[0]?.seekedDate === null &&
                                isManagerResponse[0]?.accepted === false &&
                                isManagerResponse[0]?.solution !== null)
                            : false;

                        //this is for showing the button when ERP manager declined the issue
                        const isDeclineERP =
                          isManagerResponse?.length > 0
                            ? isManagerResponse[0]?.accepted === null ||
                              isManagerResponse[0]?.accepted === false
                            : false;

                        //this is for checking the manager give some resolve and wait for ERP respond, as long as we can show PENDING only.
                        const isNullERP = canResolve(isManagerResponse?.[0]);

                        return (
                          <TableRow key={row.id}>
                            <TableCell style={{ maxWidth: "70px" }}>
                              <p className="overflowContain"> {row.slNo}</p>
                            </TableCell>
                            <TableCell style={{ maxWidth: "70px" }}>
                              <p className="overflowContain">
                                {formatReportedDate(row)}
                                <br />
                                {formatReportedTime(row)}
                              </p>
                            </TableCell>
                            <TableCell style={{ maxWidth: "150px", wordBreak: "break-word", overflowWrap: "break-word" }}>
                              <p
                                ref={addDraftRef(`description-${row.id}`)}
                                onMouseEnter={(e) =>
                                  handlePopoverOpen(
                                    e,
                                    `description-${row.id}`,
                                    row.description
                                  )
                                }
                                onMouseLeave={handlePopoverClose}
                                style={{
                                  color: "#e03131",
                                  fontWeight: "bold",
                                  textAlign: "justify",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                  whiteSpace: "normal",
                                  margin: 0,
                                }}
                              >
                                {row.description}
                              </p>
                            </TableCell>
                            <TableCell style={{ maxWidth: "100px" }}>
                              <p
                                className="overflowContain"
                                ref={addDraftRef(`department-${row.id}`)}
                                onMouseEnter={(e) =>
                                  handlePopoverOpen(
                                    e,
                                    `department-${row.id}`,
                                    row?.department
                                      ?.map((item) => item)
                                      .join(", ")
                                  )
                                }
                                onMouseLeave={handlePopoverClose}
                              >
                                {row?.department
                                  ?.map((item) => item)
                                  .join(", ")}
                              </p>
                            </TableCell>
                            <TableCell style={{ maxWidth: "100px" }}>
                              <p
                                className="overflowContain"
                                ref={addDraftRef(`reportedBy-${row.id}`)}
                                onMouseEnter={(e) =>
                                  handlePopoverOpen(
                                    e,
                                    `reportedBy-${row.id}`,
                                    row.reportedBy
                                  )
                                }
                                onMouseLeave={handlePopoverClose}
                              >
                                {row.reportedBy}
                              </p>
                            </TableCell>

                            {(isTelevision || isMD) && (
                              <TableCell style={{ maxWidth: "100px" }}>
                                <img
                                  src={row?.image}
                                  alt="issue"
                                  style={{
                                    width: "100%",
                                    height: "100px",
                                    cursor: "pointer",
                                    objectFit: "cover",
                                  }}
                                  onClick={() => handleClickOpen(row.image)}
                                />
                              </TableCell>
                            )}

                            {isTelevision || isMD ? (
                              <TableCell style={{ maxWidth: "220px" }}>
                                <Grid
                                  container
                                  item
                                  xs={12}
                                  rowGap={1}
                                  gap={2}
                                  alignItems="center"
                                >
                                  {isOpenIssue(row.status) && (
                                    <p
                                      style={{
                                        width: "100%",
                                        color: STATUS_COLORS[row.status],
                                      }}
                                    >
                                      <span style={statusStyle(row.status)}>{row.status}</span>
                                    </p>
                                  )}
                                </Grid>
                              </TableCell>
                            ) : (
                              <TableCell style={{ maxWidth: "220px" }}>
                                <Grid
                                  container
                                  item
                                  xs={12}
                                  rowGap={1}
                                  gap={2}
                                  alignItems="center"
                                >
                                  {isDeclineERP ? (
                                    showSeekDateSelect ? (
                                      <>
                                        <Button
                                          variant="outlined"
                                          color="primary"
                                          sx={{
                                            fontSize: {
                                              md: "16px",
                                              sm: "16px",
                                              xs: "12px",
                                            },
                                            width: {
                                              md: "140px",
                                              sm: "140px",
                                              xs: "100px",
                                            },
                                          }}
                                          style={{
                                            textTransform: "none",
                                            color: "#007BFF",
                                            borderColor: "#007BFF",
                                          }}
                                          onClick={() => handleSeek(row)}
                                        >
                                          Seek Time
                                        </Button>

                                        {isNullERP && (
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{
                                              fontSize: {
                                                md: "16px",
                                                sm: "16px",
                                                xs: "12px",
                                              },
                                              width: {
                                                md: "140px",
                                                sm: "140px",
                                                xs: "100px",
                                              },
                                            }}
                                            style={{
                                              textTransform: "none",
                                              background: "#28A745",
                                              color: "white",
                                            }}
                                            onClick={() => handleResolve(row)}
                                          >
                                            Resolve
                                          </Button>
                                        )}
                                      </>
                                    ) : (
                                      // Rejected: resolve again, same as pending
                                      isOpenIssue(row.status) && (
                                        <>
                                          <p
                                            style={{
                                              width: "140px",
                                              // display: "flex",
                                              // justifyContent: "center",
                                              color: STATUS_COLORS[row.status],
                                            }}
                                          >
                                            <span style={statusStyle(row.status)}>{row.status}</span>
                                          </p>

                                          {isNullERP && (
                                            <Button
                                              variant="contained"
                                              color="primary"
                                              sx={{
                                                fontSize: {
                                                  md: "16px",
                                                  sm: "16px",
                                                  xs: "12px",
                                                },
                                                width: {
                                                  md: "140px",
                                                  sm: "140px",
                                                  xs: "100px",
                                                },
                                              }}
                                              style={{
                                                textTransform: "none",
                                                background: "#28A745",
                                                color: "white",
                                              }}
                                              onClick={() => handleResolve(row)}
                                            >
                                              Resolve
                                            </Button>
                                          )}
                                        </>
                                      )
                                    )
                                  ) : (
                                    STATUS_COLORS[row.status] && (
                                      <p
                                        style={{
                                          width: "100%",
                                          // display: "flex",
                                          // justifyContent: "center",
                                          color: STATUS_COLORS[row.status],
                                        }}
                                      >
                                        <span style={statusStyle(row.status)}>{row.status}</span>
                                      </p>
                                    )
                                  )}
                                </Grid>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Typography align="center">
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Dialog open={openNew} onClose={handleClose} maxWidth="lg">
                <DialogTitle>
                  <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    style={{ position: "absolute", right: 8, top: 8 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <img
                    src={selectedImage}
                    alt="issue"
                    style={{
                      height: "100%",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                </DialogContent>
              </Dialog>

              <Popover
                sx={{
                  pointerEvents: "none",
                }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
              >
                <p style={{ padding: "15px" }}>{popoverContent}</p>
              </Popover>
            </TableContainer>
            {!isTelevision && !isMD && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 15]}
                component="div"
                count={filteredData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}

export default TaskSheetManager;
